/**
 * TD-20 Form Handler
 * JSON-driven form submission with Filestack uploads and intl-tel-input
 * 
 * Features:
 * - Form validation
 * - International phone number input (intl-tel-input)
 * - Filestack file uploads
 * - AJAX form submission
 * - Success/error handling
 */

(function() {
  'use strict';

  // Global TD20 namespace
  window.TD20 = window.TD20 || {};

  // Store for phone input instances
  TD20.phoneInputs = {};
  
  // Store for Filestack uploads
  TD20.fileUploads = {};
  
  // Filestack client instance
  TD20.filestackClient = null;

  /**
   * Initialize all TD20 forms on the page
   */
  TD20.init = function() {
    const forms = document.querySelectorAll('.td20-form');
    
    forms.forEach(function(form) {
      TD20.initForm(form);
    });
  };

  /**
   * Initialize a single form
   * @param {HTMLFormElement} form - The form element
   */
  TD20.initForm = function(form) {
    const formId = form.id;
    
    // Initialize intl-tel-input for phone fields
    TD20.initPhoneInputs(form);
    
    // Initialize Filestack dropzones
    TD20.initFilestack(form);
    
    // Add submit handler
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      TD20.handleSubmit(form);
    });
    
    // Add real-time validation
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach(function(input) {
      input.addEventListener('blur', function() {
        TD20.validateField(input);
      });
      input.addEventListener('input', function() {
        // Remove invalid state on input
        if (input.classList.contains('is-invalid')) {
          input.classList.remove('is-invalid');
        }
      });
    });
  };

  /**
   * Initialize intl-tel-input for phone fields
   * @param {HTMLFormElement} form - The form element
   */
  TD20.initPhoneInputs = function(form) {
    const phoneInputs = form.querySelectorAll('.intl-tel-input');
    
    if (phoneInputs.length === 0) return;
    
    // Check if intlTelInput is loaded
    if (typeof window.intlTelInput === 'undefined') {
      console.warn('intl-tel-input not loaded. Phone inputs will work without international formatting.');
      return;
    }
    
    phoneInputs.forEach(function(input) {
      const initialCountry = input.dataset.intlInitialCountry || 'gb';
      let preferredCountries = ['gb', 'ie', 'us'];
      
      try {
        preferredCountries = JSON.parse(input.dataset.intlPreferredCountries || '["gb", "ie", "us"]');
      } catch (e) {}
      
      const iti = window.intlTelInput(input, {
        initialCountry: initialCountry,
        preferredCountries: preferredCountries,
        separateDialCode: true,
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js'
      });
      
      // Store instance
      TD20.phoneInputs[input.id] = iti;
      
      // Update hidden field with full number on change
      input.addEventListener('countrychange', function() {
        TD20.updatePhoneHidden(input);
      });
      input.addEventListener('blur', function() {
        TD20.updatePhoneHidden(input);
      });
    });
  };

  /**
   * Update hidden field with full international phone number
   * @param {HTMLInputElement} input - The phone input element
   */
  TD20.updatePhoneHidden = function(input) {
    const iti = TD20.phoneInputs[input.id];
    if (!iti) return;
    
    const hiddenField = document.getElementById(input.id + '-full');
    if (hiddenField) {
      hiddenField.value = iti.getNumber();
    }
  };

  /**
   * Initialize Filestack dropzones
   * @param {HTMLFormElement} form - The form element
   */
  TD20.initFilestack = function(form) {
    const dropzones = form.querySelectorAll('.filestack-dropzone');
    
    if (dropzones.length === 0) return;
    
    const apiKey = form.dataset.filestackKey;
    
    // If no Filestack API key, use Base64 fallback
    if (!apiKey || apiKey === 'YOUR_FILESTACK_API_KEY') {
      console.info('Filestack not configured. Using Base64 file upload fallback.');
      TD20.initBase64Upload(form, dropzones);
      return;
    }
    
    // Check if Filestack is loaded
    if (typeof window.filestack === 'undefined') {
      console.warn('Filestack SDK not loaded.');
      return;
    }
    
    // Initialize Filestack client
    if (!TD20.filestackClient) {
      TD20.filestackClient = filestack.init(apiKey);
    }
    
    dropzones.forEach(function(dropzone) {
      const maxFiles = parseInt(dropzone.dataset.maxFiles) || 5;
      let acceptTypes = ['image/*'];
      
      try {
        acceptTypes = JSON.parse(dropzone.dataset.accept || '["image/*"]');
      } catch (e) {}
      
      // Find the hidden input and files container
      const fieldName = dropzone.id.replace('-dropzone', '');
      const hiddenInput = document.getElementById(fieldName);
      const filesContainer = document.getElementById(fieldName + '-files');
      
      // Initialize file uploads storage
      TD20.fileUploads[fieldName] = [];
      
      // Handle picker button click
      const pickerBtn = dropzone.querySelector('.filestack-picker-btn');
      if (pickerBtn) {
        pickerBtn.addEventListener('click', function(e) {
          e.preventDefault();
          TD20.openFilePicker(fieldName, maxFiles, acceptTypes);
        });
      }
      
      // Handle drag and drop
      dropzone.addEventListener('dragover', function(e) {
        e.preventDefault();
        dropzone.classList.add('dragover');
      });
      
      dropzone.addEventListener('dragleave', function() {
        dropzone.classList.remove('dragover');
      });
      
      dropzone.addEventListener('drop', function(e) {
        e.preventDefault();
        dropzone.classList.remove('dragover');
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          TD20.uploadFiles(fieldName, files, maxFiles);
        }
      });
    });
  };

  /**
   * Initialize Base64 file upload (fallback when Filestack not configured)
   * @param {HTMLFormElement} form - The form element
   * @param {NodeList} dropzones - Dropzone elements
   */
  TD20.initBase64Upload = function(form, dropzones) {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit for Base64
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    
    dropzones.forEach(function(dropzone) {
      const maxFiles = parseInt(dropzone.dataset.maxFiles) || 5;
      const fieldName = dropzone.id.replace('-dropzone', '');
      const filesContainer = document.getElementById(fieldName + '-files');
      
      // Initialize storage
      TD20.fileUploads[fieldName] = [];
      
      // Create file input element
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.multiple = true;
      fileInput.accept = 'image/*,.pdf';
      fileInput.style.display = 'none';
      fileInput.id = fieldName + '-file-input';
      dropzone.appendChild(fileInput);
      
      // Update dropzone content
      dropzone.innerHTML = `
        <div class="text-center py-3">
          <i class="fa-solid fa-cloud-upload-alt fa-2x text-muted mb-2"></i>
          <p class="mb-2">Drag and drop files here or</p>
          <button type="button" class="btn btn-sm btn-outline-primary base64-picker-btn">Browse Files</button>
          <p class="small text-muted mt-2 mb-0">Max ${maxFiles} files, 5MB each (JPG, PNG, PDF)</p>
        </div>
      `;
      dropzone.appendChild(fileInput);
      if (!filesContainer) {
        const fc = document.createElement('div');
        fc.id = fieldName + '-files';
        fc.className = 'uploaded-files mt-2';
        dropzone.parentNode.insertBefore(fc, dropzone.nextSibling);
      }
      
      // Browse button handler
      const pickerBtn = dropzone.querySelector('.base64-picker-btn');
      if (pickerBtn) {
        pickerBtn.addEventListener('click', function(e) {
          e.preventDefault();
          fileInput.click();
        });
      }
      
      // File input change handler
      fileInput.addEventListener('change', function(e) {
        TD20.handleBase64Files(fieldName, e.target.files, maxFiles, MAX_FILE_SIZE, ALLOWED_TYPES);
        fileInput.value = ''; // Reset to allow re-selecting same file
      });
      
      // Drag and drop handlers
      dropzone.addEventListener('dragover', function(e) {
        e.preventDefault();
        dropzone.classList.add('dragover');
      });
      
      dropzone.addEventListener('dragleave', function() {
        dropzone.classList.remove('dragover');
      });
      
      dropzone.addEventListener('drop', function(e) {
        e.preventDefault();
        dropzone.classList.remove('dragover');
        if (e.dataTransfer.files.length > 0) {
          TD20.handleBase64Files(fieldName, e.dataTransfer.files, maxFiles, MAX_FILE_SIZE, ALLOWED_TYPES);
        }
      });
    });
  };

  /**
   * Handle Base64 file encoding
   * @param {string} fieldName - The field name
   * @param {FileList} files - Files to process
   * @param {number} maxFiles - Maximum number of files
   * @param {number} maxSize - Maximum file size in bytes
   * @param {Array} allowedTypes - Allowed MIME types
   */
  TD20.handleBase64Files = function(fieldName, files, maxFiles, maxSize, allowedTypes) {
    const currentCount = TD20.fileUploads[fieldName] ? TD20.fileUploads[fieldName].length : 0;
    const remaining = maxFiles - currentCount;
    
    if (remaining <= 0) {
      alert('Maximum number of files reached');
      return;
    }
    
    const filesToProcess = Array.from(files).slice(0, remaining);
    
    filesToProcess.forEach(function(file) {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        alert(`File type not allowed: ${file.name}\nAllowed: JPG, PNG, GIF, WebP, PDF`);
        return;
      }
      
      // Validate file size
      if (file.size > maxSize) {
        alert(`File too large: ${file.name}\nMaximum size: 5MB`);
        return;
      }
      
      // Read file as Base64
      const reader = new FileReader();
      reader.onload = function(e) {
        const base64Data = e.target.result;
        TD20.addBase64File(fieldName, {
          filename: file.name,
          mimetype: file.type,
          size: file.size,
          data: base64Data
        });
      };
      reader.onerror = function() {
        alert(`Error reading file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Add a Base64 encoded file to the upload list
   * @param {string} fieldName - The field name
   * @param {Object} fileData - File data object
   */
  TD20.addBase64File = function(fieldName, fileData) {
    if (!TD20.fileUploads[fieldName]) {
      TD20.fileUploads[fieldName] = [];
    }
    
    TD20.fileUploads[fieldName].push(fileData);
    
    // Update hidden input with JSON
    const hiddenInput = document.getElementById(fieldName);
    if (hiddenInput) {
      const uploadData = TD20.fileUploads[fieldName].map(function(f) {
        return {
          filename: f.filename,
          mimetype: f.mimetype,
          size: f.size,
          data: f.data
        };
      });
      hiddenInput.value = JSON.stringify(uploadData);
    }
    
    // Update files display
    TD20.updateBase64FilesDisplay(fieldName);
  };

  /**
   * Update the display of uploaded Base64 files
   * @param {string} fieldName - The field name
   */
  TD20.updateBase64FilesDisplay = function(fieldName) {
    const container = document.getElementById(fieldName + '-files');
    if (!container) return;
    
    const files = TD20.fileUploads[fieldName] || [];
    
    if (files.length === 0) {
      container.innerHTML = '';
      return;
    }
    
    let html = '<div class="list-group list-group-flush">';
    files.forEach(function(file, index) {
      const sizeKb = Math.round(file.size / 1024);
      const isImage = file.mimetype.startsWith('image/');
      
      html += `
        <div class="list-group-item d-flex justify-content-between align-items-center py-2">
          <div class="d-flex align-items-center">
            ${isImage ? `<img src="${file.data}" alt="${file.filename}" class="me-2 rounded" style="width:40px;height:40px;object-fit:cover;">` : '<i class="fa-solid fa-file-pdf fa-2x text-danger me-2"></i>'}
            <div>
              <small class="d-block">${file.filename}</small>
              <small class="text-muted">${sizeKb} KB</small>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-link text-danger" onclick="TD20.removeBase64File('${fieldName}', ${index})">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
      `;
    });
    html += '</div>';
    
    container.innerHTML = html;
  };

  /**
   * Remove a Base64 file from the upload list
   * @param {string} fieldName - The field name
   * @param {number} index - File index to remove
   */
  TD20.removeBase64File = function(fieldName, index) {
    if (TD20.fileUploads[fieldName]) {
      TD20.fileUploads[fieldName].splice(index, 1);
      
      // Update hidden input
      const hiddenInput = document.getElementById(fieldName);
      if (hiddenInput) {
        if (TD20.fileUploads[fieldName].length > 0) {
          const uploadData = TD20.fileUploads[fieldName].map(function(f) {
            return {
              filename: f.filename,
              mimetype: f.mimetype,
              size: f.size,
              data: f.data
            };
          });
          hiddenInput.value = JSON.stringify(uploadData);
        } else {
          hiddenInput.value = '';
        }
      }
      
      TD20.updateBase64FilesDisplay(fieldName);
    }
  };

  /**
   * Open Filestack picker
   * @param {string} fieldName - The field name
   * @param {number} maxFiles - Maximum number of files
   * @param {Array} acceptTypes - Accepted file types
   */
  TD20.openFilePicker = function(fieldName, maxFiles, acceptTypes) {
    if (!TD20.filestackClient) {
      console.error('Filestack client not initialized');
      return;
    }
    
    const currentCount = TD20.fileUploads[fieldName] ? TD20.fileUploads[fieldName].length : 0;
    const remaining = maxFiles - currentCount;
    
    if (remaining <= 0) {
      alert('Maximum number of files reached');
      return;
    }
    
    const options = {
      maxFiles: remaining,
      accept: acceptTypes,
      uploadInBackground: false,
      onUploadDone: function(result) {
        if (result.filesUploaded) {
          result.filesUploaded.forEach(function(file) {
            TD20.addUploadedFile(fieldName, file);
          });
        }
      }
    };
    
    TD20.filestackClient.picker(options).open();
  };

  /**
   * Upload files directly (for drag and drop)
   * @param {string} fieldName - The field name
   * @param {FileList} files - Files to upload
   * @param {number} maxFiles - Maximum number of files
   */
  TD20.uploadFiles = function(fieldName, files, maxFiles) {
    if (!TD20.filestackClient) {
      console.error('Filestack client not initialized');
      return;
    }
    
    const currentCount = TD20.fileUploads[fieldName] ? TD20.fileUploads[fieldName].length : 0;
    const remaining = maxFiles - currentCount;
    
    // Limit files to remaining slots
    const filesToUpload = Array.from(files).slice(0, remaining);
    
    filesToUpload.forEach(function(file) {
      TD20.filestackClient.upload(file).then(function(result) {
        TD20.addUploadedFile(fieldName, result);
      }).catch(function(err) {
        console.error('Upload failed:', err);
      });
    });
  };

  /**
   * Add uploaded file to the list
   * @param {string} fieldName - The field name
   * @param {Object} file - Filestack file object
   */
  TD20.addUploadedFile = function(fieldName, file) {
    if (!TD20.fileUploads[fieldName]) {
      TD20.fileUploads[fieldName] = [];
    }
    
    TD20.fileUploads[fieldName].push({
      url: file.url,
      filename: file.filename,
      handle: file.handle,
      size: file.size,
      mimetype: file.mimetype
    });
    
    // Update hidden input
    const hiddenInput = document.getElementById(fieldName);
    if (hiddenInput) {
      hiddenInput.value = JSON.stringify(TD20.fileUploads[fieldName]);
    }
    
    // Update UI
    TD20.renderUploadedFiles(fieldName);
  };

  /**
   * Remove uploaded file
   * @param {string} fieldName - The field name
   * @param {number} index - File index
   */
  TD20.removeFile = function(fieldName, index) {
    if (TD20.fileUploads[fieldName]) {
      TD20.fileUploads[fieldName].splice(index, 1);
      
      // Update hidden input
      const hiddenInput = document.getElementById(fieldName);
      if (hiddenInput) {
        hiddenInput.value = JSON.stringify(TD20.fileUploads[fieldName]);
      }
      
      // Update UI
      TD20.renderUploadedFiles(fieldName);
    }
  };

  /**
   * Render uploaded files list
   * @param {string} fieldName - The field name
   */
  TD20.renderUploadedFiles = function(fieldName) {
    const container = document.getElementById(fieldName + '-files');
    if (!container) return;
    
    const files = TD20.fileUploads[fieldName] || [];
    
    if (files.length === 0) {
      container.innerHTML = '';
      return;
    }
    
    let html = '<ul class="uploaded-files-list list-unstyled mb-0 mt-2">';
    
    files.forEach(function(file, index) {
      const isImage = file.mimetype && file.mimetype.startsWith('image/');
      html += '<li class="uploaded-file d-flex align-items-center py-2 border-bottom">';
      
      if (isImage) {
        html += '<img src="' + file.url + '" alt="' + file.filename + '" class="uploaded-thumbnail me-2">';
      } else {
        html += '<i class="fas fa-file me-2 text-muted"></i>';
      }
      
      html += '<span class="filename flex-grow-1 text-truncate">' + file.filename + '</span>';
      html += '<button type="button" class="btn btn-sm btn-outline-danger ms-2" onclick="TD20.removeFile(\'' + fieldName + '\', ' + index + ')">';
      html += '<i class="fas fa-times"></i>';
      html += '</button>';
      html += '</li>';
    });
    
    html += '</ul>';
    container.innerHTML = html;
  };

  /**
   * Validate a single field
   * @param {HTMLElement} input - The input element
   * @returns {boolean} - Whether the field is valid
   */
  TD20.validateField = function(input) {
    let isValid = true;
    
    // Check required
    if (input.hasAttribute('required')) {
      if (input.type === 'checkbox') {
        isValid = input.checked;
      } else if (input.value.trim() === '') {
        isValid = false;
      }
    }
    
    // Check email format
    if (input.type === 'email' && input.value.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      isValid = emailRegex.test(input.value.trim());
    }
    
    // Check phone validation with intl-tel-input
    if (input.classList.contains('intl-tel-input') && input.value.trim() !== '') {
      const iti = TD20.phoneInputs[input.id];
      if (iti && !iti.isValidNumber()) {
        isValid = false;
      }
    }
    
    // Update UI
    if (isValid) {
      input.classList.remove('is-invalid');
      input.classList.add('is-valid');
    } else {
      input.classList.remove('is-valid');
      input.classList.add('is-invalid');
    }
    
    return isValid;
  };

  /**
   * Validate entire form
   * @param {HTMLFormElement} form - The form element
   * @returns {boolean} - Whether the form is valid
   */
  TD20.validateForm = function(form) {
    let isValid = true;
    const inputs = form.querySelectorAll('input:not([type="hidden"]), select, textarea');
    
    inputs.forEach(function(input) {
      if (!TD20.validateField(input)) {
        isValid = false;
      }
    });
    
    return isValid;
  };

  /**
   * Handle form submission
   * @param {HTMLFormElement} form - The form element
   */
  TD20.handleSubmit = function(form) {
    const formId = form.id;
    const submitBtn = document.getElementById(formId + '-submit');
    const errorDiv = document.getElementById(formId + '-error');
    const successDiv = document.getElementById(formId + '-success');
    
    // Validate form
    if (!TD20.validateForm(form)) {
      // Scroll to first invalid field
      const firstInvalid = form.querySelector('.is-invalid');
      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Show loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.querySelector('.btn-text').classList.add('d-none');
      submitBtn.querySelector('.btn-loading').classList.remove('d-none');
    }
    
    // Hide previous errors
    if (errorDiv) {
      errorDiv.classList.add('d-none');
    }
    
    // Collect form data
    const formData = new FormData(form);
    const data = {};
    
    formData.forEach(function(value, key) {
      // Handle phone fields - use the full number
      if (key.endsWith('_full')) {
        const baseName = key.replace('_full', '');
        if (value) {
          data[baseName] = value;
        }
      } else if (!data[key]) {
        data[key] = value;
      }
    });
    
    // Add timestamp
    data.submittedAt = new Date().toISOString();
    
    // Get endpoint
    const endpoint = form.dataset.td20Endpoint;
    
    if (!endpoint || endpoint === 'https://api.example.com/submit') {
      // Demo mode - just show success
      console.log('Form submission (demo mode):', data);
      setTimeout(function() {
        TD20.showSuccess(formId);
      }, 1000);
      return;
    }
    
    // Submit form
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(function(result) {
      TD20.showSuccess(formId);
    })
    .catch(function(error) {
      console.error('Form submission error:', error);
      TD20.showError(formId, 'Something went wrong. Please try again or contact us directly.');
    });
  };

  /**
   * Show success view
   * @param {string} formId - The form ID
   */
  TD20.showSuccess = function(formId) {
    const form = document.getElementById(formId);
    const submitBtn = document.getElementById(formId + '-submit');
    const successDiv = document.getElementById(formId + '-success');
    
    // Reset button state
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.querySelector('.btn-text').classList.remove('d-none');
      submitBtn.querySelector('.btn-loading').classList.add('d-none');
    }
    
    // Hide form, show success
    if (form) {
      form.classList.add('d-none');
    }
    if (successDiv) {
      successDiv.classList.remove('d-none');
    }
  };

  /**
   * Show error message
   * @param {string} formId - The form ID
   * @param {string} message - Error message
   */
  TD20.showError = function(formId, message) {
    const submitBtn = document.getElementById(formId + '-submit');
    const errorDiv = document.getElementById(formId + '-error');
    
    // Reset button state
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.querySelector('.btn-text').classList.remove('d-none');
      submitBtn.querySelector('.btn-loading').classList.add('d-none');
    }
    
    // Show error
    if (errorDiv) {
      errorDiv.querySelector('.error-message').textContent = message;
      errorDiv.classList.remove('d-none');
    }
  };

  /**
   * Reset form to initial state
   * @param {string} formId - The form ID
   */
  TD20.resetForm = function(formId) {
    const form = document.getElementById(formId);
    const successDiv = document.getElementById(formId + '-success');
    const errorDiv = document.getElementById(formId + '-error');
    
    // Reset form
    if (form) {
      form.reset();
      form.classList.remove('d-none');
      
      // Clear validation states
      const inputs = form.querySelectorAll('.is-valid, .is-invalid');
      inputs.forEach(function(input) {
        input.classList.remove('is-valid', 'is-invalid');
      });
      
      // Clear file uploads
      const dropzones = form.querySelectorAll('.filestack-dropzone');
      dropzones.forEach(function(dz) {
        const fieldName = dz.id.replace('-dropzone', '');
        TD20.fileUploads[fieldName] = [];
        TD20.renderUploadedFiles(fieldName);
        
        const hiddenInput = document.getElementById(fieldName);
        if (hiddenInput) {
          hiddenInput.value = '';
        }
      });
    }
    
    // Hide success/error
    if (successDiv) {
      successDiv.classList.add('d-none');
    }
    if (errorDiv) {
      errorDiv.classList.add('d-none');
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', TD20.init);
  } else {
    TD20.init();
  }

})();

